/**
 * SleepOverlay — v0.14.0 soft wind-down nudge.
 *
 * Appears on /park once the daily play window has been exhausted (the
 * play-session timer has crossed getSleepThresholdMinutes). Soft and
 * dismissable — NOT a hard block. Outside /park the timer doesn't tick
 * (setPlayScopeActive(false)), so nothing fires elsewhere.
 *
 * 调用关系：
 *   - Mounted by App.tsx near the route root
 *   - Reads timer via lib/playSession.ts usePlaySession() hook
 *   - Threshold persisted in localStorage + sync'd from parent admin
 *   - Nudges link out to /  or /schedule so the kid has a graceful exit
 *
 * 关键约束：
 *   - Per-route dismiss only — re-entering /park reshows the modal so
 *     the kid can't permanently silence it without re-crossing threshold.
 *   - The two action buttons (Home / Schedule) are intentional —
 *     forcing a route change makes "I'm done" feel real.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePlaySession, getSleepThresholdMinutes } from '../lib/playSession';
import { t, useLang } from '../i18n';

function isParkRoute(pathname: string): boolean {
  return pathname === '/park' || pathname.startsWith('/park?') || pathname.startsWith('/park/');
}

export default function SleepOverlay() {
  useLang();
  const { isSleepy, elapsedMs } = usePlaySession();
  const location = useLocation();
  const navigate = useNavigate();
  // Per-route dismiss — tapping "Got it" hides the modal until either
  // the path changes back into /park from outside or the timer resets.
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    setDismissed(false);
  }, [location.pathname]);

  if (!isSleepy) return null;
  // Only nag on /park — everything else stays clean per v0.14.0 spec.
  if (!isParkRoute(location.pathname)) return null;
  if (dismissed) return null;

  const minutes = Math.floor(elapsedMs / 60_000);
  const limit = getSleepThresholdMinutes();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9100,
        background:
          'radial-gradient(ellipse at center, rgba(15,18,40,0.78) 0%, rgba(8,10,28,0.92) 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        // Block ALL pointer events on the page below.
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(180deg, #fefce8, #fde68a)',
          color: '#78350f',
          borderRadius: 24,
          padding: '28px 24px 24px',
          maxWidth: 'min(92vw, 420px)',
          textAlign: 'center',
          boxShadow:
            '0 30px 60px rgba(0,0,0,0.5), inset 0 0 0 2px rgba(255,255,255,0.55)',
        }}
      >
        <div style={{ fontSize: 56, lineHeight: 1, marginBottom: 8 }}>✨</div>
        <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 8 }}>
          {t('park.softSleep.title')}
        </div>
        <div
          style={{
            fontSize: 14.5,
            lineHeight: 1.65,
            fontWeight: 600,
            whiteSpace: 'pre-line',
            marginBottom: 18,
          }}
        >
          {t('park.softSleep.body', { minutes, limit })}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            className="btn btn-primary"
            style={{
              background: 'linear-gradient(135deg, #f97316, #ea580c)',
              fontSize: 16,
              fontWeight: 800,
              padding: '12px 16px',
              borderRadius: 14,
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            {t('park.softSleep.gotoHome')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{
              background: 'linear-gradient(135deg, #10b981, #059669)',
              fontSize: 16,
              fontWeight: 800,
              padding: '12px 16px',
              borderRadius: 14,
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/schedule')}
          >
            {t('park.softSleep.gotoSchedule')}
          </button>
          <button
            type="button"
            style={{
              background: 'transparent',
              color: '#78350f',
              fontSize: 13,
              fontWeight: 600,
              padding: '8px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            onClick={() => setDismissed(true)}
          >
            {t('park.softSleep.dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
}
